import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportReactError } from '@/lib/dev-error-capture'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

const RELOAD_FLAG_KEY = 'lev-chunk-reload-attempted'

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return (
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  )
}

export default class DevErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportReactError(error.message, error.stack, info.componentStack ?? undefined)

    // Belt-and-suspenders: if a stale-chunk error bubbled past lazyWithRetry
    // (e.g. a nested dynamic import that didn't go through the wrapper),
    // force a one-shot reload so the user never has to reload manually.
    if (isChunkLoadError(error)) {
      let hasReloaded = false
      try {
        hasReloaded = window.sessionStorage.getItem(RELOAD_FLAG_KEY) === '1'
      } catch {
        hasReloaded = false
      }
      if (!hasReloaded) {
        try {
          window.sessionStorage.setItem(RELOAD_FLAG_KEY, '1')
        } catch {
          // ignore
        }
        window.location.reload()
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
          <p className="text-red-400 text-lg font-medium">Something went wrong</p>
          <button
            className="px-4 py-2 bg-purple/20 text-purple rounded hover:bg-purple/30 transition-colors"
            onClick={() => {
              try {
                window.sessionStorage.removeItem(RELOAD_FLAG_KEY)
              } catch {
                // ignore
              }
              window.location.reload()
            }}
          >
            Reload page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
